import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, MapPin, ArrowRight } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useEmbedAutoResize } from "@/lib/embed";

export default function EmbedJob() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/job/${jobId}`).then(({ data }) => setJob(data)).catch(() => setError(true));
  }, [jobId]);

  const theme = job?.embed_theme || "transparent";
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = theme === "light" ? "#F5F5F7" : "transparent";
  }, [theme]);

  useEmbedAutoResize([job, error, theme]);

  if (error) return <div className="p-6 text-[#86868B] text-sm">This position is no longer available.</div>;
  if (!job) return <div className="p-6 text-[#86868B] text-sm">Loading…</div>;

  const accent = job.accent_color || "#007AFF";
  const origin = window.location.origin;

  return (
    <div className="p-4 bg-transparent" data-testid="embed-job" data-embed-root>
      <motion.a initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        href={`${origin}/apply/${job.id}`} target="_blank" rel="noreferrer"
        className="group block bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/5 p-6 hover:shadow-[0_24px_48px_rgb(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
            <Briefcase size={22} style={{ color: accent }} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-xl tracking-tight">{job.title}</h3>
            <div className="flex items-center gap-3 text-sm text-[#86868B] mt-1.5 flex-wrap">
              {job.location && <span className="flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
              {job.years_experience && <span>{job.years_experience}</span>}
              <span>{job.employment_type}</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-[#86868B] mt-4 line-clamp-3 leading-relaxed">{job.description}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-white rounded-full px-5 py-2.5 font-semibold text-sm group-hover:gap-3 transition-all hover:brightness-110" style={{ backgroundColor: accent }}>
          Apply now <ArrowRight size={15} />
        </span>
      </motion.a>
    </div>
  );
}
